/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { pluginsActions } from '@opengeoweb/plugins';
import { soundingsActions, SoundingsTypes } from '@opengeoweb/soundings';
import {
  genericActions,
  layerActions,
  layerTypes,
  mapActions,
  mapEnums,
  syncGroupsActions,
  uiActions,
} from '@opengeoweb/store';
import { timeSeriesActions, TimeSeriesTypes } from '@opengeoweb/time-series';
import { warningsFiltersActions } from '@opengeoweb/warnings';
import type {
  ClearViewPresetLabelFiltersPayload,
  ErrorViewPresetPayload,
  FetchedViewPresetPayload,
  FetchViewPresetsPayload,
  OnSuccesViewPresetActionPayload,
  OpenViewPresetDialogPayload,
  RegisterViewPresetPayload,
  SavedViewPresetPayload,
  SaveViewPresetPayload,
  SearchWorkspaceFilter,
  SelectPluginPayload,
  SelectViewPresetPayload,
  SelectViewTemplatePayload,
  SetActiveViewPresetIdPayload,
  SetFilterChipsPayload,
  SetSelectAllFilterChip,
  SetViewPresetHasChangePayload,
  SetViewPresetInlineOverridePayload,
  SetViewPresetLabelFiltersPayload,
  ToggleSelectFilterChip,
  ToggleViewPresetListDialogPayload,
  UnregisterViewPresetPayload,
  ViewPresetEntity,
  ViewPresetsListFilter,
  ViewPresetState,
} from './types';
import { ViewPresetErrorComponent, ViewPresetErrorType } from './types';
import { WorkspacePresetAction } from '../workspace/types';
import {
  workspaceListActions,
  favouriteFilterState,
} from '../workspaceList/reducer';
import { PresetFilterType } from '../workspaceList/types';
import { togglePresetFilter } from '../workspaceList/utils';

export const defaultViewPresetsFilters: ViewPresetsListFilter[] = [
  {
    label: 'shared-filter-my-view-presets',
    id: PresetFilterType.personal,
    isSelected: true,
    isDisabled: false,
  },
  {
    label: 'shared-filter-system-view-presets',
    id: PresetFilterType.system,
    isSelected: true,
    isDisabled: false,
  },
  {
    label: 'shared-filter-shared-view-presets',
    id: PresetFilterType.shared,
    isSelected: true,
    isDisabled: false,
  },
  {
    label: 'shared-filter-template-presets',
    id: PresetFilterType.templates,
    isSelected: true,
    isDisabled: false,
  },
  favouriteFilterState,
];

export const ADMIN_VIEW_PRESET_FILTER_IDS: (PresetFilterType | 'all')[] = [
  PresetFilterType.system,
  PresetFilterType.shared,
  PresetFilterType.templates,
  PresetFilterType.favourites,
];

export const pluginViewPresetsFilter: ViewPresetsListFilter = {
  label: 'shared-filter-plugin-view-presets',
  id: PresetFilterType.plugin,
  isSelected: true,
  isDisabled: false,
};

export const viewPresetsAdapter = createEntityAdapter<ViewPresetEntity, string>(
  {
    // we use panelId as id (panelId can be mapId or viewId)
    selectId: (mapPreset) => mapPreset.panelId,
  },
);

export const initialState: ViewPresetState =
  viewPresetsAdapter.getInitialState();

const warningsPresetChangeActions = [
  warningsFiltersActions.setWarningFilters,
  warningsFiltersActions.setExpandedSectionsForPanel,
];

const syncGroupChangeActions = [
  syncGroupsActions.syncGroupRemoveTarget,
  syncGroupsActions.syncGroupAddTarget,
];
const mapPresetChangeActions = [
  layerActions.setBaseLayers,
  layerActions.layerDelete,
  layerActions.addLayer,
  layerActions.duplicateMapLayer,
  layerActions.layerChangeEnabled,
  layerActions.layerChangeName,
  layerActions.layerChangeStyle,
  layerActions.layerChangeOpacity,
  layerActions.layerChangeDimension,
  layerActions.layerChangeAcceptanceTime,
  layerActions.SetLayerLegendVisibilityPayload,
  mapActions.layerMoveLayer,
  mapActions.setAutoLayerId,
  mapActions.setAutoTimestepLayerId,
  mapActions.setAutoUpdateLayerId,
  mapActions.toggleAutoUpdate,
  mapActions.mapStartAnimation,
  mapActions.mapStopAnimation,
  mapActions.toggleTimeSliderIsVisible,
  mapActions.setTimeStep,
  mapActions.setTimeSliderSpan,
  mapActions.toggleTimeSpanAuto,
  mapActions.setAnimationDelay,
  mapActions.toggleTimestepAuto,
  mapActions.setAnimationStartTime,
  mapActions.setAnimationEndTime,
  mapActions.setAnimationLength,
  mapActions.changeOverlayer,
  uiActions.setActiveMapIdForDialog,
  uiActions.setToggleOpenDialog,
  genericActions.setBbox,
  mapActions.setBbox,
  mapActions.toggleClock,
  mapActions.toggleClockOptions,
  mapActions.setClockPositionSize,
  mapActions.setLegendPositionSize,
  mapActions.setDockedLayerManagerSize,
  mapActions.setDockedLayerManagerPosition,
  mapActions.setDockedLayerManagerDimensions,
];
const pluginPresetChangeActions = [pluginsActions.setGeoWebPluginState];
const timeSeriesPresetChangeActions = [
  timeSeriesActions.addParameter,
  timeSeriesActions.addPlot,
  timeSeriesActions.deleteParameter,
  timeSeriesActions.deletePlot,
  timeSeriesActions.moveParameter,
  timeSeriesActions.movePlot,
  timeSeriesActions.toggleParameter,
  timeSeriesActions.togglePlot,
  timeSeriesActions.patchParameter,
  timeSeriesActions.updateTitle,
  timeSeriesActions.setLocationMenuSizeAndPosition,
  timeSeriesActions.setLocationsDialogOpen,
];

const soundingsChangeActions = [
  soundingsActions.updateSelectedSource,
  soundingsActions.updateSelectedLocation,
  soundingsActions.updateDiagramType,
  soundingsActions.toggleLocationMarkers,
  soundingsActions.updateProfileDataPreferences,
  soundingsActions.changeSoundingPanelOrder,
  soundingsActions.toggleSoundingPanelVisibility,
  soundingsActions.setPanelIsOpen,
  soundingsActions.setPanelCustomWidth,
  soundingsActions.toggleLegendVisibility,
  soundingsActions.updateSelectedTime,
  soundingsActions.toggleTimeSliderVisibility,
  soundingsActions.setLocationMenuSizeAndPosition,
  soundingsActions.setLocationsMenuOpen,
];

export type SupportedMapActionOrigins =
  | layerTypes.LayerActionOrigin
  | mapEnums.MapActionOrigin;

export const supportedMapActionOrigins: SupportedMapActionOrigins[] = [
  layerTypes.LayerActionOrigin.layerManager,
  layerTypes.LayerActionOrigin.legend,
  layerTypes.LayerActionOrigin.wmsLoader,
  mapEnums.MapActionOrigin.map,
];

export type SupportedTimeSeriesActionOrigins =
  TimeSeriesTypes.TimeSeriesActionOrigin;

export const supportedTimeSeriesActionOrigins: SupportedTimeSeriesActionOrigins[] =
  [
    TimeSeriesTypes.TimeSeriesActionOrigin.timeSeriesManager,
    TimeSeriesTypes.TimeSeriesActionOrigin.timeSeriesSelect,
  ];

export type SupportedSoundingsActionOrigins =
  SoundingsTypes.SoundingsActionOrigin;

export const supportedSoundingsActionOrigins: SupportedSoundingsActionOrigins[] =
  [
    SoundingsTypes.SoundingsActionOrigin.soundingsComponent,
    SoundingsTypes.SoundingsActionOrigin.soundingsManager,
  ];

const slice = createSlice({
  initialState,
  name: 'viewPresets',
  reducers: {
    // fetch list actions
    fetchViewPresets: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<FetchViewPresetsPayload>,
    ) => {
      const { panelId } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        entity.isFetching = true;
        entity.error = undefined;

        viewPresetsAdapter.setOne(draft, entity);
      }
    },

    fetchedViewPreset: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<FetchedViewPresetPayload>,
    ) => {
      const { panelId, viewPresetId: mapPresetId } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        entity.isFetching = false;
        entity.activeViewPresetId = mapPresetId;
        entity.hasChanges = false;
        entity.error = undefined;

        viewPresetsAdapter.setOne(draft, entity);
      }
    },

    saveViewPreset: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SaveViewPresetPayload>,
    ) => {
      const { panelId } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        entity.isFetching = true;
        entity.error = undefined;

        viewPresetsAdapter.setOne(draft, entity);
      }
    },
    savedViewPreset: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SavedViewPresetPayload>,
    ) => {
      const { panelId, viewPresetId } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        entity.isFetching = false;
        entity.hasChanges = false;
        entity.activeViewPresetId = viewPresetId;

        viewPresetsAdapter.setOne(draft, entity);
      }
    },

    errorViewPreset: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<ErrorViewPresetPayload>,
    ) => {
      const { panelId, error } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        entity.error = error;
        entity.isFetching = false;

        viewPresetsAdapter.setOne(draft, entity);
      }
    },

    // map actions
    setViewPresetHasChanges: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SetViewPresetHasChangePayload>,
    ) => {
      const { panelId, hasChanges } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        entity.hasChanges = hasChanges;

        viewPresetsAdapter.setOne(draft, entity);
      }
    },
    setViewPresetInline: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SetViewPresetInlineOverridePayload>,
    ) => {
      const { panelId, isInline } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        if (isInline) {
          entity.activeViewPresetId = '';
        }
        viewPresetsAdapter.setOne(draft, entity);
      }
    },
    setActiveViewPresetId: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SetActiveViewPresetIdPayload>,
    ) => {
      const { panelId, viewPresetId: mapPresetId } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        entity.activeViewPresetId = mapPresetId;
        entity.hasChanges = false;
        viewPresetsAdapter.setOne(draft, entity);
      }
    },
    selectViewPreset: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SelectViewPresetPayload>,
    ) => {
      const { panelId, viewPresetId: mapPresetId } = action.payload;
      const entity = draft.entities[panelId];

      if (entity) {
        // only update activeMapPresetId after selecting a NEW preset
        // existing mapPresets are getting an activeMapPresetId after a succesfull fetch
        if (mapPresetId === '') {
          entity.activeViewPresetId = '';
          entity.error = undefined;
        }

        entity.isViewPresetListDialogOpen = false;

        viewPresetsAdapter.setOne(draft, entity);
      }
    },

    // the view itself is replaced in viewPresets/listener
    selectViewTemplate: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SelectViewTemplatePayload>,
    ) => {
      const { panelId } = action.payload;
      const entity = draft.entities[panelId];

      if (entity) {
        entity.activeViewPresetId = '';
        entity.error = undefined;
        entity.isViewPresetListDialogOpen = false;

        viewPresetsAdapter.setOne(draft, entity);
      }
    },

    selectViewPlugin: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SelectPluginPayload>,
    ) => {
      const { panelId, pluginMetaData } = action.payload;
      const entity = draft.entities[panelId];
      entity.activeViewPresetId = pluginMetaData?.id || '';
      entity.error = undefined;
      entity.isViewPresetListDialogOpen = false;
    },

    // action is intercepted in mapPresets/listener
    onSuccessViewPresetAction: (
      _draft: Draft<ViewPresetState>,
      _action: PayloadAction<OnSuccesViewPresetActionPayload>,
    ) => {},

    registerViewPreset: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<RegisterViewPresetPayload>,
    ) => {
      const {
        panelId: mapId,
        viewPresetId: mapPresetId,
        filters,
      } = action.payload;
      viewPresetsAdapter.setOne(draft, {
        panelId: mapId,
        activeViewPresetId: mapPresetId,
        hasChanges: false,
        isFetching: false,
        error: undefined,
        isViewPresetListDialogOpen: false,
        filters: filters ?? defaultViewPresetsFilters,
        searchQuery: '',
        filterResults: { ids: [], entities: {} },
      });
    },
    unregisterViewPreset: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<UnregisterViewPresetPayload>,
    ) => {
      const { panelId } = action.payload;
      viewPresetsAdapter.removeOne(draft, panelId);
    },
    // dialog actions
    openViewPresetDialog: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<OpenViewPresetDialogPayload>,
    ) => {
      const { viewPresetDialog } = action.payload;
      draft.viewPresetDialog = viewPresetDialog;
    },
    closeViewPresetDialog: (draft: Draft<ViewPresetState>) => {
      delete draft.viewPresetDialog;
    },
    toggleViewPresetListDialog: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<ToggleViewPresetListDialogPayload>,
    ) => {
      const { isViewPresetListDialogOpen: isViewPresetDialogOpen, panelId } =
        action.payload;
      const entity = draft.entities[panelId];

      if (entity) {
        entity.isViewPresetListDialogOpen = isViewPresetDialogOpen;
        viewPresetsAdapter.setOne(draft, entity);
      }
    },
    toggleSelectFilterChip: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<ToggleSelectFilterChip>,
    ) => {
      const { id, isSelected, panelId, isAdmin, isFavouritesEnabled } =
        action.payload;

      const entity = draft.entities[panelId];
      if (entity) {
        entity.filters = togglePresetFilter(
          entity.filters!,
          id,
          isSelected,
          isAdmin,
          isFavouritesEnabled,
        );
      }
    },

    setSelectAllFilterChip: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SetSelectAllFilterChip>,
    ) => {
      const { panelId } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        entity.filters = entity.filters!.map((filter) => {
          return {
            ...filter,
            isSelected: true,
          };
        });
      }
    },
    setFilterChips: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SetFilterChipsPayload>,
    ) => {
      const { panelId, filters } = action.payload;

      const entity = draft.entities[panelId];

      if (!entity) {
        return;
      }
      entity.filters.forEach((filter, index) => {
        const existingFilter = filters.find(
          (existingFilter) => existingFilter.id === filter.id,
        );
        const updatedFilter = {
          ...filter,
          isSelected: existingFilter
            ? existingFilter.isSelected
            : filter.isSelected,
        };
        entity.filters[index] = updatedFilter;
      });
    },
    searchFilter: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SearchWorkspaceFilter>,
    ) => {
      const { searchQuery, panelId } = action.payload;
      const entity = draft.entities[panelId];
      if (entity) {
        entity.searchQuery = searchQuery;
      }
    },
    setViewPresetLabelFilters: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<SetViewPresetLabelFiltersPayload>,
    ) => {
      const { panelId, labels } = action.payload;
      const entity = draft.entities[panelId];
      if (!entity) {
        return;
      }

      entity.labelFilters = labels;
      viewPresetsAdapter.setOne(draft, entity);
    },
    clearViewPresetLabelFilters: (
      draft: Draft<ViewPresetState>,
      action: PayloadAction<ClearViewPresetLabelFiltersPayload>,
    ) => {
      const { panelId } = action.payload;
      const entity = draft.entities[panelId];
      if (!entity) {
        return;
      }

      entity.labelFilters = undefined;
      viewPresetsAdapter.setOne(draft, entity);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(mapActions.setMapPresetError, (draft, action) => {
      const { mapId, error } = action.payload;
      const entity = draft.entities[mapId];
      if (entity) {
        entity.error = error
          ? {
              message: error,
              component: ViewPresetErrorComponent.PRESET_DETAIL,
              errorType: ViewPresetErrorType.GENERIC,
            }
          : undefined;
        entity.isFetching = false;
        viewPresetsAdapter.setOne(draft, entity);
      }
    });

    builder.addCase(
      workspaceListActions.submitFormWorkspaceActionDialogOptions,
      (draft, action) => {
        const { data } = action.payload;
        if (data.views) {
          data.views.forEach((view) => {
            if (draft.entities[view.mosaicNodeId]) {
              draft.entities[view.mosaicNodeId]!.error = undefined;
            }
          });
        }
      },
    );

    builder.addCase(
      workspaceListActions.onSuccessWorkspacePresetAction,
      (draft, action) => {
        const { action: wsAction, views } = action.payload;

        const shouldClear =
          wsAction === WorkspacePresetAction.SAVE ||
          wsAction === WorkspacePresetAction.SAVE_AS ||
          wsAction === WorkspacePresetAction.SAVE_SYSTEM_PRESET ||
          wsAction === WorkspacePresetAction.SAVE_SYSTEM_PRESET_AS ||
          wsAction === WorkspacePresetAction.EDIT;

        if (!shouldClear) {
          return;
        }

        const viewIds = (views ?? [])
          .map((view) => view.mosaicNodeId)
          .filter(Boolean) as string[];

        if (viewIds.length) {
          viewIds.forEach((panelId) => {
            const entity = draft.entities[panelId];
            if (entity) {
              entity.hasChanges = false;
              entity.activeViewPresetId = '';
              viewPresetsAdapter.setOne(draft, entity);
            }
          });
          return;
        }

        Object.values(draft.entities).forEach((entity) => {
          if (entity) {
            // eslint-disable-next-line no-param-reassign
            entity.hasChanges = false;
            // eslint-disable-next-line no-param-reassign
            entity.activeViewPresetId = '';
            viewPresetsAdapter.setOne(draft, entity);
          }
        });
      },
    );

    warningsPresetChangeActions.map((warningsAction) =>
      builder.addCase(
        warningsAction,
        (draft: Draft<ViewPresetState>, action) => {
          const { systemUpdate, panelId } = action.payload;
          if (!systemUpdate && panelId) {
            const entity = draft.entities[panelId];
            if (entity && !entity?.hasChanges) {
              entity.hasChanges = true;
              viewPresetsAdapter.setOne(draft, entity);
            }
          }
        },
      ),
    );

    syncGroupChangeActions.map((syncGroupAction) =>
      builder.addCase(
        syncGroupAction,
        (draft: Draft<ViewPresetState>, action) => {
          const { origin, targetId } = action.payload;
          if (origin === 'user') {
            const entity = draft.entities[targetId];
            if (entity && !entity?.hasChanges) {
              entity.hasChanges = true;
              viewPresetsAdapter.setOne(draft, entity);
            }
          }
        },
      ),
    );
    mapPresetChangeActions.map((mapPresetAction) =>
      builder.addCase(
        mapPresetAction,
        (draft: Draft<ViewPresetState>, action) => {
          const { mapId, origin } = action.payload;
          const isChangeOriginSupported = supportedMapActionOrigins.includes(
            origin as SupportedMapActionOrigins,
          );

          if (isChangeOriginSupported && mapId) {
            const entity = draft.entities[mapId];
            if (entity && !entity?.hasChanges) {
              entity.hasChanges = true;
              viewPresetsAdapter.setOne(draft, entity);
            }
          }
        },
      ),
    );
    timeSeriesPresetChangeActions.map((timeSeriesPresetAction) =>
      builder.addCase(
        timeSeriesPresetAction,
        (draft: Draft<ViewPresetState>, action) => {
          const { panelId, origin } = action.payload;
          const isChangeOriginSupported =
            supportedTimeSeriesActionOrigins.includes(
              origin as SupportedTimeSeriesActionOrigins,
            );

          const changesRelevantProperties =
            timeSeriesPresetAction.type ===
            timeSeriesActions.patchParameter.type
              ? ((): boolean => {
                  const { parameter } =
                    action.payload as TimeSeriesTypes.ParameterManipulationPayload;
                  const hasRelevantNonReferenceTimeChanges =
                    Object.keys(parameter).filter(
                      (key) => key !== 'id' && key !== 'instanceId',
                    ).length > 0;
                  const hasRelativeReferenceTimeChange =
                    /^latest(?:-\d+)?$/.test(parameter.instanceId || '');

                  return (
                    hasRelevantNonReferenceTimeChanges ||
                    hasRelativeReferenceTimeChange
                  );
                })()
              : true;

          if (changesRelevantProperties && isChangeOriginSupported && panelId) {
            const entity = draft.entities[panelId];
            if (entity && !entity?.hasChanges) {
              entity.hasChanges = true;
              viewPresetsAdapter.setOne(draft, entity);
            }
          }
        },
      ),
    );
    pluginPresetChangeActions.map((pluginPresetAction) =>
      builder.addCase(
        pluginPresetAction,
        (draft: Draft<ViewPresetState>, action) => {
          const { panelId } = action.payload;

          if (panelId) {
            const entity = draft.entities[panelId];
            if (entity && !entity?.hasChanges) {
              entity.hasChanges = true;
              viewPresetsAdapter.setOne(draft, entity);
            }
          }
        },
      ),
    );
    soundingsChangeActions.map((soundingsAction) =>
      builder.addCase(
        soundingsAction,
        (draft: Draft<ViewPresetState>, action) => {
          const { viewId, origin } = action.payload;
          const isChangeOriginSupported =
            supportedSoundingsActionOrigins.includes(
              origin as SupportedSoundingsActionOrigins,
            );

          if (isChangeOriginSupported && viewId && viewId !== '') {
            const entity = draft.entities[viewId];
            if (entity && !entity?.hasChanges) {
              entity.hasChanges = true;
              viewPresetsAdapter.setOne(draft, entity);
            }
          }
        },
      ),
    );
  },
});

export const { reducer: viewPresetsReducer, actions: viewPresetActions } =
  slice;
